import React, { useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardBody, CardHeader } from 'reactstrap';
import { FiMeh } from 'react-icons/fi';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ErrorMessage } from '@hookform/error-message';
import { useSelector } from 'react-redux';
import { Chart } from 'chart.js';

import AlternativeHeader from 'components/Headers/AlternativeHeader.js';
import DatePicker from '../../../components/Inputs/DatePicker';
import FlexSelect from 'components/Inputs/FlexSelect';
import DashBoardCardItem from './components/DashBoardCardItem';
import ProtocolService from '../../Protocol/service';
import PieChart from './components/Charts/PieChart';

import { useRecordTypesByUser } from 'views/RecordType/hooks/useRecordTypesByUser';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import { Controller, useForm } from 'react-hook-form';
import { ErrorFormMessage } from 'components/ErrorFormMessage';
import { dateRules } from 'views/Protocol/form/assets/functions';
import { getDateBegin } from 'views/Report/Audit/assets/getDateBegin';
import { toISOFormat } from 'utils/fuctions/formattedToISOFormatofdate';
import { CardsItemWrapper, ResponsiveFlexSelect } from './styled';
import { errorFormMessage } from 'views/ServicePanel/components/AttendanceContent/components/Modal/CloseConversation/atonsComponents/ErrorFormMessage';
import { HorizontalBarChart } from './components/Charts/HorizontalBarChart';
import { TotalProtocolsOpenOverTime } from './components/Charts/TotalProtocolsOpenOverTime';
import { PercentageProtocolClosed } from './components/Charts/PercentageProtocolClosed';
import { toast } from 'react-toastify';
import { calculateData } from '../../Report/Protocol/Synthetic/assets/CalculateAndFormatedData';
import { ProtocolsPerServiceChannel } from './components/Charts/ProtocolsPerServiceChannel';
import { TotalProtocolsPerCustomField } from './components/Charts/TotalProtocolsPerCustomField';
import GraphicProtocolsByStage from './components/Charts/GraphicProtocolsByStage';

Chart.plugins.register(ChartDataLabels);

const Dashboard = () => {
  const systemConfigurationPrimaryColor = useSelector(
    (state) => state.systemConfiguration.primary_color
  );
  const { recordTypes, isLoadingRecordTypes, getRecordTypesByUser } =
    useRecordTypesByUser(entitiesName.PROTOCOL);

  const {
    calculateProtocolsSlaByConformity,
    calculateTotalProtocolsForBarChart,
    calculateTotalProtocolsPerCriticalityForPieChart,
    calculateTotalProtocolsPerServiceTypeForPieChart,
    calculateTotalProtocolsPerServiceChannel,
    formatDataForBarChartByHour,
    formatClosedByTimeInterval,
    formatDataForBarChartByWeekday,
    formatDataForBarChartByUser,
    calculateProtocolByCustomField
  } = calculateData();

  const { control, setValue, errors, getValues, watch } = useForm();
  const [selectedRecordType, setSelectedRecordType] = useState(null);
  const [protocolData, setProtocolData] = useState(null);
  const [protocolSLAData, setProtocolSLAData] = useState(null);
  const [isUsingSLA, setIsUsingSLA] = useState(false);

  const watchDateBegin = watch('dateBegin');
  const watchDateEnd = watch('dateEnd');

  useEffect(() => {
    if (recordTypes && recordTypes.length > 0) {
      const initialRecordType = {
        label: recordTypes[0].name,
        value: recordTypes[0].id,
        is_sla_active: recordTypes[0].is_sla_active
      };
      setSelectedRecordType(initialRecordType);
      setIsUsingSLA(recordTypes[0].is_sla_active);
    }
  }, [recordTypes]);

  useEffect(() => {
    // Caso a data não esteja completa, evita o fetch
    if (
      Number(watchDateBegin?.length) < 10 ||
      Number(watchDateEnd?.length) < 10
    )
      return;

    if (!watchDateBegin || !watchDateEnd || !selectedRecordType) return;
    const paramsToGetNonSLAProtocolInfo = {
      dateBegin: watchDateBegin,
      dateEnd: watchDateEnd,
      enableCustomFields: true,
      recordType: selectedRecordType.value
        ? selectedRecordType.value
        : selectedRecordType
    };
    const paramsToGetSLAProtocolInfo = {
      dateBegin: `${toISOFormat(watchDateBegin)}`,
      dateEnd: `${toISOFormat(watchDateEnd)}`
    };
    ProtocolService.getReportProtocol(
      { ...paramsToGetNonSLAProtocolInfo },
      'synthetic'
    )
      .then((response) => {
        const data = response?.data.content;
        setProtocolData(data);
      })
      .catch((err) => {
        console.log('err', err);
        toast.error('Erro desconhecido por favor, informar o suporte!', {
          autoClose: 3000,
          closeOnClick: true
        });
      });

    if (isUsingSLA) {
      ProtocolService.getDashboardManagerInfo(
        paramsToGetSLAProtocolInfo,
        selectedRecordType.value ? selectedRecordType.value : selectedRecordType
      )
        .then((response) => {
          const data = response?.data.data;
          setProtocolSLAData(data);
        })
        .catch((err) => {
          console.log('err', err);
          toast.error('Erro desconhecido por favor, informar o suporte!', {
            autoClose: 3000,
            closeOnClick: true
          });
        });
    }
  }, [watchDateBegin, watchDateEnd, selectedRecordType, isUsingSLA]);

  useEffect(() => {
    getRecordTypesByUser();
  }, [getRecordTypesByUser]);

  const handleRecordTypeChange = (selectedOption) => {
    setSelectedRecordType(selectedOption);
    setIsUsingSLA(selectedOption.is_sla_active);
  };

  useEffect(() => {
    if (recordTypes) {
      const selectedType = recordTypes.find(
        (type) => type.id === selectedRecordType
      );
      if (selectedType) {
        setProtocolSLAData(selectedType.slaData);
        setIsUsingSLA(selectedType.is_sla_active);
      }
    }
  }, [recordTypes, selectedRecordType]);

  const getSelectOptionsDTO = () => {
    if (!recordTypes) return [];
    return recordTypes.map((group) => ({
      label: group.name,
      value: group.id,
      is_sla_active: group.is_sla_active
    }));
  };

  const processFormatDataForBarChartByUser = useMemo(
    () => ({
      ...formatDataForBarChartByUser(protocolData?.totalProtocolsPerUser),
      labelFromUsers: true
    }),
    [protocolData]
  );

  const renderContent = () => {
    if (protocolData && protocolSLAData) {
      return (
        <>
          <Row
            className="mb-3"
            style={{
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <CardsItemWrapper>
              <DashBoardCardItem
                title="Total de protocolos"
                text={`${protocolSLAData ? protocolSLAData.totalProtocols : 0}`}
                icon="ni ni-single-copy-04"
              />
              <DashBoardCardItem
                title="Total de protocolos em aberto"
                text={`${protocolSLAData ? protocolSLAData.openProtocols : 0}`}
                icon="ni ni-single-copy-04"
              />
              <DashBoardCardItem
                title="Total de protocolos fechados"
                text={`${
                  protocolSLAData ? protocolSLAData.closedProtocols : 0
                }`}
                icon="ni ni-single-copy-04"
              />
              <DashBoardCardItem
                title="Media diária de protocolos em aberto"
                text={`${
                  protocolSLAData ? protocolSLAData.dailyOpenProtocols : 0
                }`}
                icon="ni ni-single-copy-04"
              />
              <DashBoardCardItem
                title="Media diária de protocolos fechados"
                text={`${
                  protocolSLAData ? protocolSLAData.dailyClosedProtocols : 0
                }`}
                icon="ni ni-single-copy-04"
              />
              <DashBoardCardItem
                title="Tempo médio para atender protocolos"
                text={`${
                  protocolSLAData
                    ? protocolSLAData.averageTimeToLeaveInitialStage
                    : 0
                }h`}
                icon="ni ni-time-alarm"
              />
              <DashBoardCardItem
                title="Mediana do tempo de solução"
                text={`${
                  protocolSLAData ? protocolSLAData.medianSolutionTime : 0
                }h`}
                icon="ni ni-time-alarm"
              />
            </CardsItemWrapper>
          </Row>
          <Row className="mb-3">
            <Col md="12">
              <Card>
                <CardHeader>
                  <h6 className="surtitle">Visão Geral</h6>
                  <h5 className="h3 mb-0">
                    Distribuição de Protocolos por Status
                  </h5>
                </CardHeader>
                <CardBody>
                  <GraphicProtocolsByStage protocolData={protocolData} />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md="6">
              <Card>
                {protocolSLAData && (
                  <PieChart
                    formattedDataProtocols={
                      calculateProtocolsSlaByConformity({
                        conformingProtocols:
                          protocolSLAData.conformingProtocols,
                        nonConformingProtocols:
                          protocolSLAData.nonConformingProtocols
                      }) || []
                    }
                    chartTittle="Protocolos Conformes x Não Conformes"
                  />
                )}
              </Card>
            </Col>
            {protocolData && (
              <Col md="6">
                <HorizontalBarChart
                  title="Protocolos por usuário"
                  data={processFormatDataForBarChartByUser}
                />
              </Col>
            )}
          </Row>

          <Row className="mb-3">
            <Col md="6">
              <Card>
                {protocolSLAData && (
                  <PieChart
                    formattedDataProtocols={
                      calculateTotalProtocolsPerCriticalityForPieChart(
                        protocolSLAData.pieChartDataCriticality
                      ) || []
                    }
                    chartTittle="Protocolos por criticidade"
                  />
                )}
              </Card>
            </Col>
            <Col md="6">
              <Card>
                {protocolSLAData && (
                  <PieChart
                    formattedDataProtocols={
                      calculateTotalProtocolsPerServiceTypeForPieChart(
                        protocolSLAData.pieChartDataServiceType
                      ) || []
                    }
                    chartTittle="Protocolos por classificação de serviço"
                  />
                )}
              </Card>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md="8">
              <Card>
                {protocolSLAData && (
                  <TotalProtocolsOpenOverTime
                    protocolData={
                      calculateTotalProtocolsForBarChart(
                        protocolSLAData?.openProtocolsPerDay
                      ) || []
                    }
                  />
                )}
              </Card>
            </Col>
            <Col md="4">
              <Card>
                {protocolSLAData && (
                  <PercentageProtocolClosed
                    closedProtocolsPercentage={
                      protocolSLAData?.closedProtocolsPercentage
                    }
                  />
                )}
              </Card>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md="6">
              <Card>
                {protocolSLAData && (
                  <HorizontalBarChart
                    title="Protocolos por Intervalo de Tempo"
                    data={formatClosedByTimeInterval(
                      protocolSLAData?.solutionTimeCategories
                    )}
                  />
                )}
              </Card>
            </Col>
            <Col md="6">
              <Card>
                {protocolData && (
                  <ProtocolsPerServiceChannel
                    data={
                      calculateTotalProtocolsPerServiceChannel(
                        protocolData?.totalProtocolsPerServiceChannel
                      ) || []
                    }
                  />
                )}
              </Card>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md="6">
              <Card>
                {protocolData && (
                  <HorizontalBarChart
                    title="Protocolos por hora"
                    data={formatDataForBarChartByHour(
                      protocolData?.totalProtocolsPerHour
                    )}
                  />
                )}
              </Card>
            </Col>
            <Col md="6">
              <Card>
                {protocolData && (
                  <HorizontalBarChart
                    title="Protocolos por dia da semana"
                    data={formatDataForBarChartByWeekday(
                      protocolData?.totalProtocolsPerWeekday
                    )}
                  />
                )}
              </Card>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <TotalProtocolsPerCustomField
                customFieldsData={protocolData?.reportCustomFieldsMultiple}
                calculateProtocolByCustomField={calculateProtocolByCustomField}
              />
            </Col>
          </Row>
        </>
      );
    } else if (protocolData) {
      return (
        <>
          <Row className="mb-3">
            <Col>
              <Card>
                <CardHeader>
                  <h6 className="surtitle">Visão Geral</h6>
                  <h5 className="h3 mb-0">
                    Distribuição de Protocolos por Status
                  </h5>
                </CardHeader>
                <CardBody>
                  <GraphicProtocolsByStage protocolData={protocolData} />
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              {protocolData && (
                <HorizontalBarChart
                  title="Protocolos por usuário"
                  data={processFormatDataForBarChartByUser}
                />
              )}
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md="6">
              <Card>
                {protocolData && (
                  <ProtocolsPerServiceChannel
                    data={
                      calculateTotalProtocolsPerServiceChannel(
                        protocolData?.totalProtocolsPerServiceChannel
                      ) || []
                    }
                  />
                )}
              </Card>
            </Col>
            <Col md="6">
              <Card>
                {protocolData && (
                  <HorizontalBarChart
                    title="Protocolos por hora"
                    data={formatDataForBarChartByHour(
                      protocolData?.totalProtocolsPerHour
                    )}
                  />
                )}
              </Card>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col>
              <Card>
                {protocolData && (
                  <HorizontalBarChart
                    title="Protocolos por dia da semana"
                    data={formatDataForBarChartByWeekday(
                      protocolData?.totalProtocolsPerWeekday
                    )}
                  />
                )}
              </Card>
            </Col>
          </Row>
          <Row>
            <Col md="12">
              <TotalProtocolsPerCustomField
                customFieldsData={protocolData?.reportCustomFieldsMultiple}
                calculateProtocolByCustomField={calculateProtocolByCustomField}
              />
            </Col>
          </Row>
        </>
      );
    } else {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <p
            className="mb-1 text-muted"
            style={{
              padding: '0.5rem',
              fontWeight: 'bold',
              fontSize: '1.2rem'
            }}
          >
            Nenhum dado disponível.
          </p>
        </div>
      );
    }
  };

  return (
    <>
      <AlternativeHeader />

      {!isLoadingRecordTypes && !recordTypes?.length ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <p
            className="mb-1 text-muted"
            style={{
              padding: '0.5rem',
              fontWeight: 'bold',
              fontSize: '1.2rem'
            }}
          >
            Você não tem acesso a nenhum tipo de protocolo.
          </p>
          <FiMeh size={25} color={systemConfigurationPrimaryColor} />
        </div>
      ) : (
        <Container className="mt--6" fluid>
          <Card className="mb-3">
            <CardBody>
              <Row className="mb-3">
                <Col md="4">
                  <label className="form-control-label">
                    Tipo de registro*
                  </label>
                  <Controller
                    render={(props) => (
                      <ResponsiveFlexSelect>
                        <FlexSelect
                          dataOptions={getSelectOptionsDTO() || []}
                          isMulti={false}
                          value={props.value}
                          valueController={setValue}
                          fieldName="selectedRecordType"
                          labelName="label"
                          valueName="value"
                          onChange={handleRecordTypeChange}
                          closeMenuOnSelect={true}
                          placeholder="Tipo de registro do protocolo"
                          classNamePrefix="react-select"
                        />
                      </ResponsiveFlexSelect>
                    )}
                    control={control}
                    defaultValue={
                      selectedRecordType ? selectedRecordType : null
                    }
                    rules={{ required: 'Campo obrigatório' }}
                    name="selectedRecordType"
                  />
                  <ErrorMessage
                    errors={errors}
                    name="selectedRecordType"
                    render={({ message }) => errorFormMessage(message)}
                  />
                </Col>
                <Col md="4">
                  <label className="form-control-label">Data inicial</label>
                  <Controller
                    render={(props) => (
                      <DatePicker
                        fieldName="dateBegin"
                        valueController={setValue}
                        defaultValue={props.value}
                      />
                    )}
                    control={control}
                    name="dateBegin"
                    rules={dateRules}
                    defaultValue={getDateBegin()}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="dateBegin"
                    render={({ message }) => (
                      <ErrorFormMessage message={message} />
                    )}
                  />
                </Col>
                <Col md="4">
                  <label className="form-control-label">Data final</label>
                  <Controller
                    render={(props) => (
                      <DatePicker
                        fieldName="dateEnd"
                        valueController={setValue}
                        defaultValue={props.value}
                      />
                    )}
                    control={control}
                    name="dateEnd"
                    defaultValue={new Date().toLocaleDateString('pt-br')}
                    rules={{
                      ...dateRules,
                      validate: async (value) => {
                        if (
                          new Date(await toISOFormat(value, true)) <
                          new Date(
                            await toISOFormat(getValues()['dateBegin'], true)
                          )
                        ) {
                          return 'A data final não pode ser anterior à inicial!';
                        } else {
                          return true;
                        }
                      }
                    }}
                  />
                  <ErrorMessage
                    errors={errors}
                    name="dateEnd"
                    render={({ message }) => (
                      <ErrorFormMessage message={message} />
                    )}
                  />
                </Col>
              </Row>
            </CardBody>
          </Card>
          <CardBody>{renderContent()}</CardBody>
        </Container>
      )}
    </>
  );
};

export default Dashboard;
